<template>
  <div class="tasklist-view" data-testid="tasklist__view">
    <div class="tasklist-view__table-area">
    <TasklistTable
      v-if="!loading"
      ref="tasklistTable"
      :table-headersfilter="resolvedTableHeadersfilter"
      :filter-by-tab="filterByTab"
      :filter-options-data="filterOptionsSource"
      :paginated-filtered-items="paginatedFilteredItems"
      :items-per-page="itemsPerPage"
      :search="search"
      :table-filter-header="tableFilterHeader"
      :page_details="page_details"
      :selected-tasks="selectedTasks"
      :is-partial-selection="isPartialSelection"
      :is-all-selected="isAllSelected"
      :bulk-locked="bulkLocked"
      :user-type="userType"
      :metadata="metadata"
      :userid="userid"
      :right="right"
      :file-names-list="fileNamesList"
      :location-list="locationList"
      :user-map="userMap"
      :sticky-column-count="stickyColumnCount"
      @select-all-change="select"
      @filter-item="filterPart"
      @clear-item="onTableFilterClear"
      @filter-active-change="onFilterActiveChange"
      @column-menu-change="onColumnMenuChange"
      @restore-sticky-column-classes="applyStickyColumnClasses"
      @refresh="$emit('refresh')"
      @sort-order="sortingOrder"
      @column-reorder="$emit('column-reorder', $event)"
      @update:selected-tasks="updateSelectedTasks"
      @navigate-task="$emit('navigate-task', $event)"
      @open-upload-drawer="onOpenUploadDrawer"
      @open-bulk-upload-box="$emit('open-bulk-upload-box', $event)"
      @delete-checklist-item="$emit('delete-checklist-item', $event)"
      @open-bulk-upload="onOpenBulkUpload"
      @workpaper-template="$emit('workpaper-template')"
      @download-single-task-files="$emit('download-single-task-files', $event)"
      @change-checklist-status="$emit('change-checklist-status', $event)"
      @open-approve-reject="onOpenApproveReject"
      @open-helpdesk="onOpenHelpdesk"
      @open-link-bot="onOpenLinkBot"
      @unlink-bot="$emit('unlink-bot', $event)"
      @view-task-details="$emit('view-task-details', $event)"
      @edit-task="$emit('edit-task', $event)"
      @row-edit-preparer="openRowUserAssignment('preparer', $event)"
      @row-edit-approver="onRowEditApprover"
    />
    <v-skeleton-loader
    v-else
    type="table-heading, list-item-two-line, article, article, article"
    class="my-4"
    data-testid="tasklist__table-skeleton"
    />
    <RowUserAssignmentDialog
      v-if="rowUserAssignmentMounted"
      :visible.sync="rowUserAssignmentVisible"
      :assign-type="rowUserAssignmentType"
      :task="rowUserAssignmentTask"
      :users="activeAssignmentUsers"
      :cycle-id="projectId"
      @saved="$emit('refresh')"
    />
    <TaskApproveRejectDialog
      v-if="approveRejectMounted"
      :visible.sync="approveRejectVisible"
      :mode="approveRejectMode"
      :task="approveRejectTask"
      :tasks="approveRejectTasks"
      :metadata="metadata"
      @saved="onApproveRejectSaved"
    />
    <bulk-selection-bar
      v-if="selectedTasks.length > 0"
      :selected-count="selectedTasks.length"
      :section-label="bulkSelectionSectionLabel"
      :actions="bulkSelectionActions"
      :actions-tooltip="bulkSelectionActionsTooltip"
      :loading="loading"
      test-id-prefix="tasklist"
      @clear-selection="clearSelection"
      @action="onBulkSelectionAction"
    />
    </div>
    <TasklistDrawers
      ref="tasklistDrawers"
      :project-id="projectId"
      :metadata="metadata"
      :selected-tasks="selectedTasks"
      :page-loading="loading"
      :custom-columns="customColumns"
      :due-date-min-date="dueDateMinDate"
      :due-date-max-date="dueDateMaxDate"
      :user-map="userMap"
      :user-details="userDetails"
      :transaction-matching-list="transactionMatchingList"
      :bot-list="botList"
      :lease-schedule-items="leaseScheduleItems"
      :location-list="locationList"
      :category-acc="categoryAcc"
      :user-type="userType"
      :get-names-by-ids="getNamesByIds"
      :get-location-by-id="getLocationById"
      :workpaper-file-names-list="fileNamesList"
      v-bind="drawerBindings"
      @refresh="$emit('refresh')"
      @update:selectedTasks="$emit('update:selected-tasks', $event)"
      @page-loading-change="$emit('page-loading-change', $event)"
      @bulk-manage-user="$emit('bulk-manage-user', $event)"
      @bulk-edit-open="$emit('bulk-edit-open')"
      @checklist-file-change="$emit('checklist-file-change', $event)"
      @checklist-reupload="$emit('checklist-reupload')"
      @checklist-download-template="$emit('checklist-download-template')"
      @checklist-upload="$emit('checklist-upload')"
      @checklist-cancel="$emit('checklist-cancel')"
      @update:checklistShowDataWithError="$emit('update:checklistShowDataWithError', $event)"
      @bulk-edit-file-change="$emit('bulk-edit-file-change', $event)"
      @bulk-edit-reupload="$emit('bulk-edit-reupload')"
      @bulk-edit-download-template="$emit('bulk-edit-download-template')"
      @bulk-edit-upload="$emit('bulk-edit-upload')"
      @bulk-edit-cancel="$emit('bulk-edit-cancel')"
      @update:bulkEditShowDataWithError="$emit('update:bulkEditShowDataWithError', $event)"
      @workpaper-files-change="$emit('workpaper-files-change', $event)"
      @update:workpaperFileRemark="$emit('update:workpaperFileRemark', $event)"
      @update:workpaperUploadWithoutFile="$emit('update:workpaperUploadWithoutFile', $event)"
      @workpaper-download-template="$emit('workpaper-download-template')"
      @workpaper-download-file="$emit('workpaper-download-file', $event)"
      @workpaper-submit-without-file="$emit('workpaper-submit-without-file')"
      @workpaper-submit-with-files="$emit('workpaper-submit-with-files')"
      @clear-selected="$emit('update:selected-tasks', [])"
    />
  </div>
</template>

<script>
import './TasklistView.scss';
import TasklistTable from './TasklistTable.vue';
import TasklistDrawers from './TasklistDrawers.vue';
import { RowUserAssignmentDialog, TaskApproveRejectDialog } from './components';
import { parseUrlQueryFilters } from '../../../utils/tasklistUtils';
import { getCurrentPageFromState } from '../../../utils/taskDetailNavigation';
import { DEFAULT_TASKLIST_ITEMS_PER_PAGE } from '../../../utils/tasklistConstants';
import { TASK_EDIT_MESSAGES } from '../../../utils/constants';
import { getTaskTypeDisplay } from '../../../utils/utils';
import { TASKLIST_USER_TYPES } from '../../../utils/listConstants';
import { CHECKLIST_STATUS_ACTION, attachApproverPinnedFilterItems, USER_ROLE, APPROVER_TAG } from '../../../utils/constants';
import {
  filterByPipelineStage,
  filterByTaskNameSearch,
  hasApprovedTaskEditLocked,
  isUserApproverOnTask,
  normalizePriorityFilterValue,
  TASK_TYPE_FILTER,
  applyTasklistColumnFilters,
  isTasklistColumnVisible,
} from '../../../utils/tasklistHelpers';
import { resolveEnforceThreshold } from '@/modules/financialclose/utils/enforceThresholdHelpers';
import {
  activeUserIdsFromUserRoles,
  normalizeUserIdList,
} from '../../Setup/TaskList/utils/assignmentRoles';

/** Develop `.th-width-48` is 3rem at a 16px root. Do not resolve 3rem (that is 42px here). */
const TASKLIST_CHECKBOX_COLUMN_PX = 48;

/**
 * Header column-actions control. Vuetify `.v-btn--icon.v-size--default` is 36px
 * wide (VBtn.sass) and `.data-table-action__column-actions-btn` adds `ml-1` (4px).
 * Used when the button is not laid out yet. A rendered button uses its own box.
 */
const TASKLIST_COLUMN_FILTER_ICON_PX = 40;

function assignmentIdsForDisplay(task, role) {
  if (Array.isArray(task?.user_roles) && task.user_roles.length) {
    const tag = role === USER_ROLE.APPROVER ? APPROVER_TAG.L1 : null;
    return activeUserIdsFromUserRoles(task.user_roles, role, tag);
  }
  if (role === USER_ROLE.PREPARER) return normalizeUserIdList(task?.preparer);
  if (role === USER_ROLE.APPROVER) return normalizeUserIdList(task?.approver);
  return [];
}

export default {
  name: 'TasklistView',
  components: {
    TasklistTable,
    TasklistDrawers,
    RowUserAssignmentDialog,
    TaskApproveRejectDialog,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    metadata: {
      type: Object,
      default: () => ({}),
    },
    projectId: {
      type: String,
      default: '',
    },
    userid: {
      type: String,
      default: '',
    },
    userType: {
      type: String,
      default: TASKLIST_USER_TYPES.PREPARER,
    },
    right: {
      type: String,
      default: '',
    },
    tableHeadersfilter: {
      type: Array,
      default: () => [],
    },
    selectedTabStage: {
      type: [Number, String],
      default: null,
    },
    overDueDate: {
      type: Boolean,
      default: false,
    },
    searchQuery: {
      type: String,
      default: '',
    },
    customColumns: {
      type: Array,
      default: () => [],
    },
    taskListData: {
      type: Array,
      default: () => [],
    },
    locationList: {
      type: Array,
      default: () => [],
    },
    categoryAcc: {
      type: Array,
      default: () => [],
    },
    userMap: {
      type: Object,
      default: () => ({}),
    },
    usersList: {
      type: Array,
      default: () => [],
    },
    page_details: {
      type: Object,
      default: () => ({}),
    },
    search: {
      type: String,
      default: '',
    },
    selectedTasks: {
      type: Array,
      default: () => [],
    },
    selectedFilter: {
      type: String,
      default: TASK_TYPE_FILTER.ALL,
    },
    initialItemsPerPage: {
      type: Number,
      default: 50,
    },
    dueDateMinDate: { type: String, default: '' },
    dueDateMaxDate: { type: String, default: '' },
    userDetails: { type: Object, default: () => ({ activeUsers: [], activeUserIds: [] }) },
    transactionMatchingList: { type: Array, default: () => [] },
    botList: { type: Array, default: () => [] },
    leaseScheduleItems: { type: Array, default: () => [] },
    /** Upload/edit drawer state passed from shell (see TasksLandingPage tasklistDrawerBindings). */
    drawerBindings: { type: Object, default: () => ({}) },
    showConversation: {
      type: Boolean,
      default: true,
    },
    /** Not every row is loaded yet: select-all, bulk actions and column filters are disabled. */
    bulkLocked: {
      type: Boolean,
      default: false,
    },
    /**
     * Tasklist refresh: the open page is not in the loaded slice yet, so show page 1.
     * Back from task detail leaves this false and keeps the saved page.
     */
    snapUnloadedPageToFirst: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      masterTaskList: [],
      processedTaskList: [],
      paginatedFilteredItems: [],
      fileNamesList: [],
      hasActiveTableFilter: false,
      itemsPerPage: this.initialItemsPerPage || DEFAULT_TASKLIST_ITEMS_PER_PAGE,
      tableFilterHeader: {},
      // Immediate filterbysearch runs before created and slices with this.page.
      page: this._detailNavigationPageFromStore(),
      windowHeight: typeof window !== 'undefined' ? window.innerHeight : 800,
      stickyColumnCount: 3,
      columnMenuOpen: false,
      selectedFilterLocal: this.selectedFilter,
      rowUserAssignmentVisible: false,
      rowUserAssignmentType: 'preparer',
      rowUserAssignmentTask: null,
      // Dialogs mount on first open and stay mounted (close animation).
      rowUserAssignmentMounted: false,
      approveRejectMounted: false,
      approveRejectVisible: false,
      approveRejectMode: 'approve',
      approveRejectTask: null,
      approveRejectTasks: [],
    };
  },
  created() {
    // Seed column filters from Vuex before TasklistTable/DataTableAction mounts
    this._seedTableFilterFromStore();
  },
  computed: {
    resolvedTableHeadersfilter() {
      return attachApproverPinnedFilterItems(this.tableHeadersfilter);
    },
    activeAssignmentUsers() {
      return this.userDetails?.activeUsers?.length
        ? this.userDetails.activeUsers
        : this.usersList;
    },
    isAllSelected() {
      const source = this.filterbysearch || [];
      if (!source.length || !this.selectedTasks.length) return false;
      if (this.selectedTasks.length < source.length) return false;
      const selectedIds = new Set(this.selectedTasks.map((t) => t?._id));
      return source.every((item) => selectedIds.has(item?._id));
    },
    isPartialSelection() {
      const source = this.filterbysearch || [];
      if (!this.selectedTasks.length || !source.length || this.isAllSelected) {
        return false;
      }
      const selectedIds = new Set(this.selectedTasks.map((t) => t?._id));
      return source.some((item) => selectedIds.has(item?._id));
    },
    filteredTasklist() {
      let array = [];
      if (this.userType === TASKLIST_USER_TYPES.PREPARER) {
        const uid = String(this.userid);
        array = this.processedTaskList.filter((item) =>
          assignmentIdsForDisplay(item, USER_ROLE.PREPARER).includes(uid)
        );
      } else if (this.userType === TASKLIST_USER_TYPES.APPROVER) {
        array = this.processedTaskList.filter((item) => isUserApproverOnTask(item, this.userid));
      } else if (this.userType === TASKLIST_USER_TYPES.MANAGER) {
        array = this.processedTaskList;
      } else {
        array = [...this.processedTaskList];
      }

      if (this.overDueDate) {
        return array.filter((item) => item?.duedays < 0);
      }
      return [...array];
    },
    filterByTab() {
      return filterByPipelineStage(this.filteredTasklist, this.selectedTabStage);
    },
    /**
     * Role + tab + task-type scoped rows for column filter option lists (pre-column-filter).
     * Matches rows visible before column filters so option lists stay consistent.
     */
    filterOptionsSource() {
      const taskTypeFilter = this.selectedFilterLocal;
      let array = this._tasksForSelectedFilter(this.masterTaskList, taskTypeFilter);
      if (this.userType === TASKLIST_USER_TYPES.PREPARER) {
        const uid = String(this.userid);
        array = array.filter((item) =>
          assignmentIdsForDisplay(item, USER_ROLE.PREPARER).includes(uid)
        );
      } else if (this.userType === TASKLIST_USER_TYPES.APPROVER) {
        array = array.filter((item) => isUserApproverOnTask(item, this.userid));
      }
      if (this.overDueDate) {
        array = array.filter((item) => item?.duedays < 0);
      }
      return filterByPipelineStage(array, this.selectedTabStage);
    },
    filterbysearch() {
      return filterByTaskNameSearch(this.filterByTab, this.searchQuery);
    },
    isBulkBarManager() {
      const userId = this.$store.state.user?.id;
      const managerList = this.metadata?.managerlist;
      if (!Array.isArray(managerList) || !userId) return false;
      return managerList.some((id) => String(id) === String(userId));
    },
    isBulkEditLocked() {
      return hasApprovedTaskEditLocked(this.selectedTasks);
    },
    areBulkEditActionsDisabled() {
      return this.loading || this.bulkLocked || this.isBulkEditLocked;
    },
    areBulkActionsDisabled() {
      return this.loading || this.bulkLocked;
    },
    bulkSelectionSectionLabel() {
      return this.userType === TASKLIST_USER_TYPES.MANAGER ? 'EDIT FIELDS' : '';
    },
    bulkSelectionActionsTooltip() {
      if (this.userType !== TASKLIST_USER_TYPES.MANAGER || !this.isBulkEditLocked) {
        return null;
      }
      return {
        show: true,
        locked: true,
        text: TASK_EDIT_MESSAGES.APPROVED_LOCKED,
      };
    },
    bulkSelectionActions() {
      const editDisabled = this.areBulkEditActionsDisabled;

      if (this.userType === TASKLIST_USER_TYPES.MANAGER) {
        const actions = [
          {
            id: 'preparer',
            label: 'Preparer',
            iconSrc: '/img/icons/fbc-icons/PreparerIcon.svg',
            disabled: editDisabled,
            testId: 'tasklist__bulk-edit-preparer-btn',
            labelTestId: 'tasklist__bulk-edit-preparer-label',
          },
          {
            id: 'approver',
            label: 'Approver',
            iconSrc: '/img/icons/fbc-icons/ApproverIcon.svg',
            disabled: editDisabled,
            testId: 'tasklist__bulk-edit-approver-btn',
            labelTestId: 'tasklist__bulk-edit-approver-label',
          },
          {
            id: 'bulk-update-duedate',
            label: 'Due Date',
            iconSrc: '/img/icons/fbc-icons/CalenderDateIcon.svg',
            disabled: editDisabled,
            testId: 'tasklist__bulk-edit-duedate-btn',
            labelTestId: 'tasklist__bulk-edit-duedate-label',
          },
          {
            id: 'bulk-edit-threshold',
            label: 'Threshold',
            iconSrc: '/img/icons/fbc-icons/ThresholdIcon.svg',
            disabled: editDisabled,
            testId: 'tasklist__bulk-edit-threshold-enforcement-btn',
            labelTestId: 'tasklist__bulk-edit-threshold-enforcement-label',
          },
        ];

        if (this.isBulkBarManager) {
          actions.push({
            id: 'bulk-edit-tasks',
            label: 'Bulk Edit Task',
            iconSrc: '/img/icons/fbc-icons/BulkeditTaskIcon.svg',
            disabled: editDisabled,
            testId: 'tasklist__bulk-edit-tasks-btn',
            labelTestId: 'tasklist__bulk-edit-tasks-label',
          });
        }

        return actions;
      }

      if (this.userType === TASKLIST_USER_TYPES.APPROVER) {
        return [
          {
            id: 'approve',
            label: 'Approve/Sign-off',
            iconSrc: '/img/icons/fbc-icons/ApproveIcon.svg',
            disabled: this.areBulkActionsDisabled,
            testId: 'tasklist__bulk-approve-btn',
            labelTestId: 'tasklist__bulk-approve-label',
          },
          {
            id: 'reject',
            label: 'Reject',
            iconSrc: '/img/icons/fbc-icons/RejectIcon.svg',
            disabled: this.areBulkActionsDisabled,
            testId: 'tasklist__bulk-reject-btn',
            labelTestId: 'tasklist__bulk-reject-label',
          },
        ];
      }

      const actions = [
        {
          id: 'bulk-workpaper-upload',
          label: 'Bulk Upload',
          iconSrc: '/img/icons/UploadICon.svg',
          disabled: this.areBulkActionsDisabled,
          testId: 'tasklist__bulk-upload-btn',
          labelTestId: 'tasklist__bulk-upload-label',
        },
      ];

      if (this.isBulkBarManager) {
        actions.push({
          id: 'bulk-unlink-bot',
          label: `Unlink ${this.$constants.BOT_NAME}`,
          icon: 'mdi-link-off',
          disabled: this.areBulkActionsDisabled,
          testId: 'tasklist__bulk-unlink-bot-btn',
          labelTestId: 'tasklist__bulk-unlink-bot-label',
        });
      }

      return actions;
    },
  },
  watch: {
    taskListData: {
      handler() {
        this.formatData();
        // Reapply the locked widths. Do not measure again because the list grew.
        this.$nextTick(() => this.updateStickyOffsets());
      },
      immediate: true,
    },
    userType() {
      this.$nextTick(() => this.updateStickyOffsets());
    },
    projectId() {
      this.$nextTick(() => this.updateStickyOffsets());
    },
    selectedFilter(val) {
      this.selectedFilterLocal = val;
      this.clearSelectionOnFilterChange();
    },
    searchQuery() {
      this.clearSelectionOnFilterChange();
    },
    selectedTabStage() {
      this.clearSelectionOnFilterChange();
    },
    overDueDate() {
      this.clearSelectionOnFilterChange();
    },
    initialItemsPerPage(val) {
      if (typeof val === 'number' && val > 0) {
        this.itemsPerPage = val;
      }
    },
    tableHeadersfilter: {
      handler() {
        this.syncFiltersWithHeaders();
        // Same visible columns: write the existing lock back. A filter edit
        // changes header state, not the column set, so do not measure.
        this.$nextTick(() => {
          if (this.hasLockedColumnWidths()) {
            this.applyLockedColumnWidths();
            return;
          }
          setTimeout(() => this.updateStickyOffsets(), 10);
        });
      },
      deep: true,
    },
    paginatedFilteredItems: {
      handler() {
        this.$nextTick(() => this.updateStickyOffsets());
        this.$emit('paginated-list-change', this.paginatedFilteredItems);
      },
    },
    filterbysearch: {
      handler(list) {
        if (this._isTearingDown) return;
        const source = Array.isArray(list) ? list : [];
        // Seed current page immediately (same pattern as Cycles.vue). Parent
        // Pagination remounts after Horizon→List switch; without this seed the
        // table stays empty until (or if) Pagination re-emits paginated-data.
        this.seedPaginatedItems(source);
        this.$emit('pagination-source-change', {
          list: source,
          itemsPerPage: this.itemsPerPage,
        });
      },
      immediate: true,
    },
    itemsPerPage(val) {
      if (this._isTearingDown) return;
      const source = this.filterbysearch || [];
      this.seedPaginatedItems(source);
      this.$emit('pagination-source-change', {
        list: source,
        itemsPerPage: val,
      });
    },
  },
  mounted() {
    this._handleResize = () => {
      this.windowHeight = window.innerHeight;
      this.scheduleStickyUpdate();
    };
    window.addEventListener('resize', this._handleResize, { passive: true });
    this.__onColumnResizeEnd = () => {
      if (!this.__columnResizing) return;
      this.__columnResizing = false;
      this.commitUserResizedColumnWidths();
      this.applyLockedColumnWidths();
    };
    document.addEventListener('mouseup', this.__onColumnResizeEnd, true);

    this.$nextTick(() => {
      this.updateStickyOffsets();
      setTimeout(() => this.updateStickyOffsets(), 50);
      setTimeout(() => this.updateStickyOffsets(), 120);
      setTimeout(() => this.updateStickyOffsets(), 300);
      setTimeout(() => this.updateStickyOffsets(), 500);

      const headerRow =
        this.$el.querySelector('.tasklist-table .v-data-table__wrapper > table > thead > tr') ||
        this.$el.querySelector('.v-data-table__wrapper > table > thead > tr');
      if (headerRow) {
        this.__headerMutationObserver = new MutationObserver(() => {
          this.__attachHeaderDragListeners();
          this.scheduleStickyUpdate();
        });
        this.__headerMutationObserver.observe(headerRow, { childList: true });
        const ResizeObs = window.ResizeObserver;
        if (ResizeObs) {
          this.__headerResizeObserver = new ResizeObs(() => this.scheduleStickyUpdate());
          this.__headerResizeObserver.observe(headerRow);
        }
        this.__attachHeaderDragListeners();
      }
    });
  },
  beforeDestroy() {
    // Prevent filterbysearch watcher from emitting [] to the parent while
    // tearing down for Horizon — that wipe left list rows empty on switch-back.
    this._isTearingDown = true;
    if (this._handleResize) {
      window.removeEventListener('resize', this._handleResize);
      this._handleResize = null;
    }
    if (this.__onColumnResizeEnd) {
      document.removeEventListener('mouseup', this.__onColumnResizeEnd, true);
      this.__onColumnResizeEnd = null;
    }
    if (this.__headerMutationObserver) {
      this.__headerMutationObserver.disconnect();
      this.__headerMutationObserver = null;
    }
    if (this.__headerResizeObserver) {
      this.__headerResizeObserver.disconnect();
      this.__headerResizeObserver = null;
    }
  },
  methods: {
    /**
     * Seed visible page rows from the filtered source list.
     * Keeps the table populated when parent Pagination is unmounted (Horizon)
     * or has not yet remounted after switching back to List.
     */
    seedPaginatedItems(sourceList) {
      const source = Array.isArray(sourceList) ? sourceList : [];
      const perPage = this.itemsPerPage > 0 ? this.itemsPerPage : 50;
      // No rows yet (first paint, or a checklist replace): keep the saved page.
      // A loaded list filtered down to nothing still starts over at page 1.
      if (!source.length) {
        this.paginatedFilteredItems = [];
        if (this.taskListData?.length) {
          this.page = 1;
        }
        return;
      }
      const maxPage = Math.max(1, Math.ceil(source.length / perPage));
      let page = Math.max(this.page || 1, 1);
      if (page > maxPage) {
        // Refresh replaced the list with the first loaded slice. Page 8 is not in
        // that slice yet — start at page 1 instead of the last loaded page.
        page = this.snapUnloadedPageToFirst ? 1 : maxPage;
      }
      this.page = page;
      const start = (page - 1) * perPage;
      this.paginatedFilteredItems = source.slice(start, start + perPage);
    },
    formatData() {
      const customColumns = this.customColumns;
      if (!this.taskListData || this.taskListData.length === 0) {
        console.log('[TasklistView]: taskListData is empty');
      }
      this.processedTaskList = (this.taskListData || []).map((item, index) => {
        const matchedLocation =
          item.location && Array.isArray(this.locationList)
            ? this.locationList.find((loc) => loc._id === item.location)
            : null;
        const formatted = {
          ...item,
          priority: normalizePriorityFilterValue(item.priority),
          __keys: index,
          __formatedkey: index,
          indexno: index + 1,
          approverids: this.getNamesByIds(assignmentIdsForDisplay(item, USER_ROLE.APPROVER)),
          closed_date: this.$nova.formatDate(item?.closed_date),
          created_at: this.$nova.formatDate(item.created_at),
          created_by: item.created_by?.split('T')[0],
          duedate: item.duedate?.split('T')[0],
          duedate1: this.$nova.formatDate(item.duedate),
          locationItem: matchedLocation ? [matchedLocation] : [{ name: '' }],
          locationName: matchedLocation?.name || item.locationName || '',
          preparerids: this.getNamesByIds(assignmentIdsForDisplay(item, USER_ROLE.PREPARER)),
          stage1: this.getStageText(item.stage).text,
          statusdetail: this.getStatusText(item.statusdetail),
          task_type: getTaskTypeDisplay(item.task_type),
          submited_on: this.$nova.formatDate(item?.submited_on),
          no_of_evidence: item.no_of_evidence || 0,
          updated_at: item.updated_at?.split('T')[0],
          updated_by: item.updated_by?.split('T')[0],
          enforceThreshold: resolveEnforceThreshold(item, item.task_type),
        };
        (Array.isArray(customColumns) ? customColumns : []).forEach((field) => {
          const key = field.columnName;
          if (!(key in formatted)) {
            formatted[key] = item[key] ?? '';
          }
        });
        return formatted;
      });
      this.masterTaskList = this.processedTaskList;
      this.handleFilterChange(this.selectedFilterLocal);
      // Re-apply column filters on the post-handleFilterChange list (keeps selectedFilterLocal)
      if (this.tableFilterHeader && Object.keys(this.tableFilterHeader).length > 0) {
        this.processedTaskList = this._applyTableColumnFilters(this.processedTaskList);
      }
    },
    formateData() {
      return this.formatData();
    },
    getNamesByIds(ids) {
      if (!Array.isArray(ids)) {
        if (typeof ids === 'string') {
          ids = [ids];
        } else if (ids == null) {
          return [];
        } else {
          return [String(ids)];
        }
      }
      return ids.map((id) => this.userMap[id]?.name || id).filter(Boolean);
    },
    getLocationById(id) {
      const location = this.locationList?.find((loc) => loc._id === id);
      return location?.name || '';
    },
    getItemData(item, key) {
      return item[key];
    },
    getStageText(stage) {
      if (stage === 0) return { text: 'Pending Submission', color: '', class: 'pending-sub-chip' };
      if (stage === 1) return { text: 'Processing', color: '', class: 'pending-approval-chip' };
      if (stage === 2) return { text: 'Pending Approval', color: '', class: 'pending-approval-chip' };
      if (stage === 3) return { text: 'Approved', color: '', class: 'closed-task-chip' };
      if (stage === -1) return { text: 'Rejected', color: 'lightred', class: 'error--text cancelled-chip' };
      return { text: 'Unknown', color: 'grey', class: '' };
    },
    getStatusText(status) {
      if (status === 'pending_not_due') return 'Pending Submission Not Due';
      if (status === 'pending_over_due') return 'Pending Submission Overdue';
      if (status === 'pending_due') return 'Pending Submission Due';
      if (status === 'pendingapproval_due') return 'Pending Approval Due';
      if (status === 'pendingapproval_not_due') return 'Pending Approval Not Due';
      if (status === 'pendingapproval_over_due') return 'Pending Approval Overdue';
      return 'approved';
    },
    handleFilterChange(filterValue) {
      this.selectedFilterLocal = filterValue;
      this.processedTaskList = this._tasksForSelectedFilter(this.masterTaskList, filterValue);
    },
    /**
     * Apply selectedFilterLocal (task-type chrome filter) to a task list.
     * Shared by handleFilterChange and filterOptionsSource.
     */
    _tasksForSelectedFilter(tasks, filterValue = this.selectedFilterLocal) {
      const list = Array.isArray(tasks) ? tasks : [];
      if (!filterValue || filterValue === TASK_TYPE_FILTER.ALL) return [...list];
      const normalTaskDisplayName = getTaskTypeDisplay(
        this.$constants.TASK_LIST_CONSTANTS.TASK_TYPES.NORMAL
      );
      if (filterValue === this.$constants.TASK_LIST_CONSTANTS.TASK_TYPES.ACCOUNT_RECONCILIATION) {
        return list.filter(
          (task) =>
            task.task_type ===
            this.$constants.TASK_LIST_CONSTANTS.TASK_TYPES.ACCOUNT_RECONCILIATION
        );
      }
      if (filterValue === this.$constants.TASK_LIST_CONSTANTS.TASK_TYPES.NORMAL) {
        return list.filter((task) => task.task_type === normalTaskDisplayName);
      }
      return [...list];
    },
    select(isSelected) {
      if (isSelected && this.bulkLocked) return;
      if (isSelected) {
        this.updateSelectedTasks([...this.filterbysearch]);
      } else {
        this.clearSelection();
      }
    },
    updateSelectedTasks(tasks) {
      this.$emit('update:selected-tasks', tasks);
    },
    clearSelection() {
      this.$emit('update:selected-tasks', []);
    },
    /**
     * Filter apply/clear always drops the whole selection.
     * BulkActionBar is shown only while selectedTasks is non-empty, so this closes it.
     */
    clearSelectionOnFilterChange() {
      if (!this.selectedTasks.length) return;
      this.clearSelection();
    },
    setPaginatedData(paginationInfo) {
      if (paginationInfo && typeof paginationInfo === 'object' && 'data' in paginationInfo) {
        this.paginatedFilteredItems = paginationInfo.data || [];
        this.page = paginationInfo.page || 1;
      } else {
        this.paginatedFilteredItems = paginationInfo || [];
      }
    },
    onFilterActiveChange(isActive) {
      this.hasActiveTableFilter = isActive;
      this.$emit('filter-active-change', isActive);
    },
    filterPart(item) {
      // Ignore empty filter-item from DataTableAction remount when an active filter already exists
      const incomingEmpty = !item || (typeof item === 'object' && Object.keys(item).length === 0);
      if (incomingEmpty && this.tableFilterHeader && Object.keys(this.tableFilterHeader).length > 0) {
        return;
      }
      const nextFilter = item || {};
      // DataTableAction.mounted forces this emit with the seeded filter.
      // That is not a user change, so the page restored from task detail stays.
      const filterSignature = (filter) => {
        const cleaned = this._validateFilterKeys(filter);
        const sorted = Object.keys(cleaned).sort().reduce((acc, key) => {
          acc[key] = cleaned[key];
          return acc;
        }, {});
        return JSON.stringify(sorted);
      };
      const unchanged = filterSignature(nextFilter) === filterSignature(this.tableFilterHeader || {});
      this.tableFilterHeader = nextFilter;
      this.handleFilterChange(this.selectedFilterLocal);
      if (this.tableFilterHeader && Object.keys(this.tableFilterHeader).length > 0) {
        this.processedTaskList = this._applyTableColumnFilters(this.processedTaskList);
      }
      if (unchanged) return;
      this.page = 1;
      this.emitListViewStateChange();
      this.$emit('pagination-reset');
      this.clearSelectionOnFilterChange();
    },
    sortingOrder(key, order) {
      this.processedTaskList = this.sortFun(this.masterTaskList, key, order);
    },
    sortFun(array, key, order = 'ascending') {
      return array.slice().sort((a, b) => {
        const valA = a[key];
        const valB = b[key];
        const normalizedA = typeof valA === 'string' ? valA.toLowerCase() : valA;
        const normalizedB = typeof valB === 'string' ? valB.toLowerCase() : valB;
        if (normalizedA < normalizedB) return order === 'ascending' ? -1 : 1;
        if (normalizedA > normalizedB) return order === 'ascending' ? 1 : -1;
        return 0;
      });
    },
    setTableFilterHeader(filter) {
      this.tableFilterHeader = this._validateFilterKeys(filter);
      this.emitListViewStateChange();
    },
    /** Snapshot of list chrome state for Vuex listViewState (back-navigation). */
    getListViewSnapshot() {
      return {
        tableFilterHeader: JSON.parse(JSON.stringify(this.tableFilterHeader || {})),
        itemsPerPage: this.itemsPerPage,
        detailNavigationPage: this.page,
      };
    },
    /** Apply column filters including Approver Auto Approved sentinel. */
    _applyTableColumnFilters(rows) {
      return applyTasklistColumnFilters(
        rows,
        this.tableFilterHeader,
        this.userMap,
        (nextRows, filters) => this.$nova.filterByDynamicCriteria(nextRows, filters)
      );
    },
    /** True when page size and (validated) column filters already match `partial`. */
    isListViewSnapshotApplied(partial = {}) {
      if (typeof partial.itemsPerPage === 'number' && partial.itemsPerPage > 0
        && partial.itemsPerPage !== this.itemsPerPage) {
        return false;
      }
      const filter = partial.tableFilterHeader;
      if (!filter || Object.keys(filter).length === 0) return true;
      return JSON.stringify(this._validateFilterKeys(filter))
        === JSON.stringify(this.tableFilterHeader || {});
    },
    /** Restore list table state from saved listViewState slice. */
    applyListViewSnapshot(partial = {}) {
      if (typeof partial.itemsPerPage === 'number' && partial.itemsPerPage > 0) {
        this.itemsPerPage = partial.itemsPerPage;
      }
      if (partial.tableFilterHeader && Object.keys(partial.tableFilterHeader).length > 0) {
        this.tableFilterHeader = this._validateFilterKeys(partial.tableFilterHeader);
        if (this.masterTaskList?.length) {
          this.handleFilterChange(this.selectedFilterLocal);
          this.processedTaskList = this._applyTableColumnFilters(this.processedTaskList);
          // Back-nav restore keeps detailNavigationPage. User filter clears still reset it.
          this.emitListViewStateChange();
        }
      }
    },
    /**
     * Page saved when a row was opened. 1 when this project has no saved page.
     * @returns {number}
     */
    _detailNavigationPageFromStore() {
      const projectId = this.projectId;
      if (!projectId || !this.$store?.getters?.['tasks/getListViewState']) return 1;
      return getCurrentPageFromState(this.$store.getters['tasks/getListViewState'](projectId));
    },
    /**
     * Prefill tableFilterHeader from Vuex so DataTableAction mounts with the
     * correct selectfilter (avoids empty init wiping saved filters).
     */
    _seedTableFilterFromStore() {
      const projectId = this.projectId;
      if (!projectId || !this.$store?.getters?.['tasks/getListViewState']) return;
      const saved = this.$store.getters['tasks/getListViewState'](projectId);
      const filter = saved?.tableFilterHeader;
      if (filter && typeof filter === 'object' && Object.keys(filter).length > 0) {
        this.tableFilterHeader = this._validateFilterKeys(filter);
      }
      if (typeof saved?.itemsPerPage === 'number' && saved.itemsPerPage > 0) {
        this.itemsPerPage = saved.itemsPerPage;
      }
      // After itemsPerPage's watcher. An empty list must not leave page at 1.
      const page = saved?.detailNavigationPage;
      if (typeof page === 'number' && page > 0) {
        this.page = page;
        const source = this.filterbysearch || [];
        if (source.length) {
          this.seedPaginatedItems(source);
        }
      }
    },
    emitListViewStateChange() {
      this.$emit('list-view-state-change', this.getListViewSnapshot());
    },
    /**
     * Drop column filters when their column is hidden or no longer filterable
     * (e.g. after refresh reloads saved layout without session-only column toggles).
     */
    syncFiltersWithHeaders() {
      if (this._isTearingDown) return;

      const cleaned = this._validateFilterKeys(this.tableFilterHeader);
      const prev = this.tableFilterHeader || {};
      const changed =
        Object.keys(prev).length !== Object.keys(cleaned).length
        || Object.keys(prev).some((key) => prev[key] !== cleaned[key]);
      if (!changed) return;

      this.tableFilterHeader = cleaned;
      this.handleFilterChange(this.selectedFilterLocal);
      if (Object.keys(cleaned).length > 0) {
        this.processedTaskList = this.$nova.filterByDynamicCriteria(
          this.processedTaskList,
          cleaned
        );
      }
      this.page = 1;
      this.emitListViewStateChange();
      this.$emit('pagination-reset');
      this.clearSelectionOnFilterChange();
    },
    _validateFilterKeys(filter) {
      if (!filter || typeof filter !== 'object') return {};
      const validHeaderValues = new Set();
      const headersToCheck = this.resolvedTableHeadersfilter || [];
      headersToCheck.forEach((h) => {
        if (!h?.value) return;
        if (h.visible === false || h.filterable === false) return;
        validHeaderValues.add(h.value);
        if (h?.value_en) validHeaderValues.add(h.value_en);
      });
      if (validHeaderValues.size === 0) {
        return headersToCheck.length === 0 ? filter : {};
      }
      const cleanedFilter = {};
      Object.keys(filter).forEach((filterKey) => {
        if (validHeaderValues.has(filterKey)) {
          cleanedFilter[filterKey] = filter[filterKey];
        } else {
          console.log('[TasklistView]: Removed stale filter key:', filterKey);
        }
      });
      return cleanedFilter;
    },
    /** User cleared filters in DataTableAction — bypass empty filter-item guard and persist. */
    onTableFilterClear() {
      this.tableFilterHeader = {};
      if (this.masterTaskList?.length) {
        this.handleFilterChange(this.selectedFilterLocal);
      }
      this.page = 1;
      this.$emit('clear-table-filter');
      this.emitListViewStateChange();
      this.$emit('pagination-reset');
      this.clearSelectionOnFilterChange();
    },
    clearTableFilter() {
      const tableRef = this.$refs.tasklistTable?.$refs?.dataTableAction;
      if (tableRef) {
        tableRef.clearFilter();
        return;
      }
      // DataTableAction not mounted yet (loading / remount)
      this.onTableFilterClear();
    },
    setFileNamesList(list) {
      this.fileNamesList = Array.isArray(list) ? list : [];
    },
    applyUrlQueryFilters() {
      try {
        const urlFilters = parseUrlQueryFilters({
          query: this.$route.query,
          standardParams: ['cid', 'view', 'userType'],
          keyMap: {
            location: 'locationName',
            status: 'stage1',
            sub_category: 'subcategory',
            due_in_days: 'duedays',
            preparer: 'preparerids',
            approver: 'approverids',
          },
          locationList: this.locationList,
          usersList: this.usersList,
          tableHeadersfilter: this.resolvedTableHeadersfilter,
          masterTaskList: this.masterTaskList,
        });

        if (!urlFilters) {
          return;
        }

        this.tableFilterHeader = { ...this.tableFilterHeader, ...urlFilters };

        if (this.masterTaskList?.length) {
          this.filterPart(this.tableFilterHeader);
        } else {
          console.log('[TasklistView]: masterTaskList is empty, filters not applied');
        }
      } catch (err) {
        console.log('[TasklistView]: Error applying URL filters:', err);
      }
    },
    __attachHeaderDragListeners() {
      try {
        const headerRow =
          this.$el.querySelector('.tasklist-table .v-data-table__wrapper > table > thead > tr') ||
          this.$el.querySelector('.v-data-table__wrapper > table > thead > tr');
        if (!headerRow) return;
        Array.from(headerRow.children).forEach((th) => {
          if (!th.__stickyDragBound) {
            th.addEventListener('dragstart', () => {
              this.__headerDragging = true;
            });
            th.addEventListener('dragend', () => {
              this.__headerDragging = false;
              requestAnimationFrame(() => setTimeout(() => this.updateStickyOffsets(), 30));
            });
            th.__stickyDragBound = true;
          }
          const handle = th.querySelector('.resizer-handle');
          if (!handle || handle.__columnResizeBound) return;
          handle.addEventListener('mousedown', () => {
            this.__columnResizing = true;
            if (!this.isCheckboxHeader(th)) th.dataset.userResized = '1';
          });
          handle.__columnResizeBound = true;
        });
      } catch (e) {
        /* silent */
      }
    },
    onColumnMenuChange(isOpen) {
      this.columnMenuOpen = !!isOpen;
      // Opening or applying a filter re-renders header classes. Reapply the
      // existing lock. Do not measure: the open menu changes header DOM.
      if (!this.hasLockedColumnWidths()) {
        if (!isOpen) this.$nextTick(() => this.updateStickyOffsets());
        return;
      }
      this.$nextTick(() => {
        if (this.hasLockedColumnWidths()) this.applyLockedColumnWidths();
      });
    },
    getStickyTableElements() {
      const table =
        this.$el?.querySelector('.tasklist-table .v-data-table__wrapper > table') ||
        this.$el?.querySelector('.v-data-table__wrapper > table');
      if (!table) return null;
      const headerRow = table.querySelector('thead tr');
      if (!headerRow) return null;
      return { table, headerRow };
    },
    headerCellsOf(headerRow) {
      return Array.from(headerRow?.children || []).filter((el) => el.tagName === 'TH');
    },
    getStickyHeaderTargets(headerRow) {
      const count = Number(this.stickyColumnCount || 3);
      const visible = this.headerCellsOf(headerRow).filter((th) => {
        const rect = th.getBoundingClientRect();
        return th.offsetParent !== null && rect.width > 0.5;
      });
      if (!visible.length) return null;
      return { count, targets: visible.slice(0, count) };
    },
    restoreStickyColumnClassesFromDataset(table) {
      const count = Number(this.stickyColumnCount || 3);
      const edgeSeq = String(count - 1);
      table.querySelectorAll('thead th[data-sticky-seq]').forEach((th) => {
        th.classList.add('dyn-sticky-col');
        th.classList.toggle('dyn-sticky-col-edge', th.dataset.stickySeq === edgeSeq);
      });
      table.querySelectorAll('tbody td[data-sticky-seq]').forEach((td) => {
        td.classList.add('dyn-sticky-col');
        td.classList.toggle('dyn-sticky-col-edge', td.dataset.stickySeq === edgeSeq);
      });
    },
    /**
     * Pixel length from a computed style. Non-lengths (none, auto) are 0.
     * @param {string} value
     * @returns {number}
     */
    cssPx(value) {
      const n = parseFloat(value);
      return Number.isFinite(n) && n > 0 ? n : 0;
    },
    /**
     * Nowrap text width. Independent of the table's width:100% column stretch.
     * @param {HTMLElement|null|undefined} el
     * @returns {number}
     */
    measureNowrapText(el) {
      if (!el) return 0;
      const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
      if (!text) return 0;
      const probe = document.createElement('span');
      const cs = window.getComputedStyle(el);
      probe.style.cssText = 'position:fixed;left:-9999px;top:0;visibility:hidden;white-space:nowrap;width:max-content;pointer-events:none;';
      probe.style.font = cs.font;
      probe.style.letterSpacing = cs.letterSpacing;
      probe.style.textTransform = cs.textTransform;
      probe.textContent = text;
      document.body.appendChild(probe);
      const width = probe.getBoundingClientRect().width;
      probe.remove();
      return width;
    },
    /**
     * Resolve a develop stylesheet length. px and rem use the root font size;
     * vh uses the viewport height. Not a table cell's stretched border box.
     * @param {string} length
     * @returns {number}
     */
    cssLengthToPx(length) {
      const raw = String(length || '').trim();
      const n = parseFloat(raw);
      if (!Number.isFinite(n) || n <= 0) return 0;
      if (raw.endsWith('rem')) {
        const root = parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
        return n * root;
      }
      if (raw.endsWith('vh')) {
        return (n / 100) * (window.innerHeight || 0);
      }
      if (raw.endsWith('px')) return n;
      return 0;
    },
    /**
     * Column-actions control rendered for a sortable or filterable header
     * (`.data-table-action__column-actions-btn`). Checkbox headers have none.
     * Laid-out width is the button box plus horizontal margin (36px + `ml-1`).
     * @param {HTMLElement} th
     * @returns {number}
     */
    columnFilterIconPx(th) {
      if (!th || this.isCheckboxHeader(th)) return 0;
      const btn = th.querySelector('.data-table-action__column-actions-btn');
      if (btn) {
        const cs = window.getComputedStyle(btn);
        const measured = btn.offsetWidth
          + this.cssPx(cs.marginLeft)
          + this.cssPx(cs.marginRight);
        if (measured > 0) return Math.round(measured);
        return TASKLIST_COLUMN_FILTER_ICON_PX;
      }
      const value = th.getAttribute('data-column-value') || '';
      const header = (this.resolvedTableHeadersfilter || []).find((item) => item?.value === value);
      if (header && header.visible !== false && (header.filterable || header.sortable)) {
        return TASKLIST_COLUMN_FILTER_ICON_PX;
      }
      return 0;
    },
    /**
     * Label text plus the header cell's own padding and border.
     * Inner flex padding is not a slot for the filter control.
     * @param {HTMLElement} th
     * @returns {number}
     */
    headerLabelBoxPx(th) {
      const label = th.querySelector('.data-table-action__header-text');
      const cs = window.getComputedStyle(th);
      const chrome = this.cssPx(cs.paddingLeft) + this.cssPx(cs.paddingRight)
        + this.cssPx(cs.borderLeftWidth) + this.cssPx(cs.borderRightWidth);
      return this.measureNowrapText(label) + chrome;
    },
    /**
     * True when the filter/sort button is in the measured header box.
     * Opacity 0 still occupies space. A zero box means it is not laid out yet.
     * @param {HTMLElement} th
     * @returns {boolean}
     */
    columnActionsButtonLaidOut(th) {
      const btn = th?.querySelector('.data-table-action__column-actions-btn');
      return !!(btn && btn.offsetWidth > 0);
    },
    /**
     * Locked width for a non-sticky column.
     * Every sortable or filterable header reserves the 40px control unless that
     * button already has a layout box. `offsetWidth` is body text, the 5vh
     * min, or padding — it is not proof the label sits beside the icon.
     * @param {HTMLElement} th
     * @param {number} measured
     * @returns {number}
     */
    nonStickyColumnWidth(th, measured) {
      if (this.isCheckboxHeader(th)) return TASKLIST_CHECKBOX_COLUMN_PX;
      const labelBox = this.headerLabelBoxPx(th);
      if (this.columnActionsButtonLaidOut(th)) {
        const btn = th.querySelector('.data-table-action__column-actions-btn');
        const btnCs = window.getComputedStyle(btn);
        const buttonPx = btn.offsetWidth
          + this.cssPx(btnCs.marginLeft)
          + this.cssPx(btnCs.marginRight);
        return Math.max(measured, Math.ceil(labelBox + buttonPx));
      }
      if (this.columnFilterIconPx(th) <= 0) return measured;
      return Math.ceil(Math.max(measured, labelBox) + TASKLIST_COLUMN_FILTER_ICON_PX);
    },
    /**
     * Pixel width the user dragged, keyed by column value. Zero when they have not.
     * @param {HTMLElement|null|undefined} th
     * @returns {number}
     */
    userResizedWidthPx(th) {
      const value = th?.getAttribute('data-column-value') || '';
      if (!value || !this.__userResizedWidthsByValue) return 0;
      const saved = this.__userResizedWidthsByValue.get(value);
      return saved > 0 ? saved : 0;
    },
    /**
     * Read a finished column resize (max-width set, width cleared) into the lock.
     * Ignores the truncation default max-width while width is still applied.
     */
    commitUserResizedColumnWidths() {
      const elements = this.getStickyTableElements();
      if (!elements) return;
      const headerCells = this.headerCellsOf(elements.headerRow);
      if (!this.__userResizedWidthsByValue) this.__userResizedWidthsByValue = new Map();
      headerCells.forEach((th, index) => {
        if (th.dataset.userResized !== '1' || this.isCheckboxHeader(th)) return;
        const value = th.getAttribute('data-column-value') || '';
        // doResize writes only max-width and clears width. A click that did not
        // move leaves the locked width in place — do not treat that as a drag.
        const dragged = !th.style.width && !!th.style.maxWidth;
        const px = Math.round(parseFloat(th.style.maxWidth));
        if (!dragged || !(px > 0) || !value) {
          delete th.dataset.userResized;
          return;
        }
        this.__userResizedWidthsByValue.set(value, px);
        this.__userResizedColumns = true;
        const entry = (this.__lockedColumnWidths || []).find((item) => item.index === index);
        if (entry && !entry.checkbox) {
          entry.width = px;
          entry.userResized = true;
        }
      });
    },
    isCheckboxHeader(th) {
      // Column filters render v-checkbox inside the header. Do not treat that
      // as the select-all column — it locks every filtered column to 48px.
      if (!th || th.tagName !== 'TH') return false;
      if (th.getAttribute('data-column-value')) return false;
      return th.getAttribute('data-testid') === 'data-table-action__selectall-header'
        || th.classList.contains('th-width-48');
    },
    isCheckboxColumnCell(el) {
      if (!el) return false;
      if (el.tagName === 'TH') return this.isCheckboxHeader(el);
      return el.classList.contains('tasklist__table-row-leading-cell');
    },
    /**
     * Sticky width from develop CSS. The checkbox column is a fixed 48px
     * (develop `.th-width-48` at a 16px root), not a rem measurement.
     * Task Name is 45vh plus the filter control when that header shows one.
     * Task Type stays the header/body text width with the 5vh header floor,
     * and is not capped at 280px.
     * Never getBoundingClientRect of the width:100% table.
     * @param {HTMLElement} th
     * @param {NodeListOf<HTMLElement>|HTMLElement[]} bodyRows
     * @param {number} columnIndex
     * @returns {number}
     */
    stickyColumnWidth(th, bodyRows, columnIndex) {
      const columnValue = th.getAttribute('data-column-value') || '';
      if (this.isCheckboxHeader(th)) {
        return TASKLIST_CHECKBOX_COLUMN_PX;
      }
      if (columnValue === 'description') {
        // Keep in sync with TasklistTable.scss description min-width:
        // 45vh text box plus the 40px column-actions control.
        return Math.round(this.cssLengthToPx('45vh') + this.columnFilterIconPx(th));
      }
      // DataTableAction.scss .th-min-width-5vh and
      // .data-table-action__header-cell { min-width: 5vh }
      const floor = this.cssLengthToPx('5vh');
      const content = this.uncappedColumnContentWidth(th, bodyRows, columnIndex);
      return Math.max(Math.round(floor), content);
    },
    /**
     * Header plus rendered body text. No 280px cap and no table border box.
     * @param {HTMLElement} th
     * @param {NodeListOf<HTMLElement>|HTMLElement[]} bodyRows
     * @param {number} columnIndex
     * @returns {number}
     */
    uncappedColumnContentWidth(th, bodyRows, columnIndex) {
      const cs = window.getComputedStyle(th);
      const pad = this.cssPx(cs.paddingLeft) + this.cssPx(cs.paddingRight)
        + this.cssPx(cs.borderLeftWidth) + this.cssPx(cs.borderRightWidth);
      const label = th.querySelector('.data-table-action__header-text');
      const actions = th.querySelector('.data-table-action__column-actions-btn');
      let w = this.measureNowrapText(label) + pad;
      if (actions && actions.offsetWidth > 0) {
        // In-flow control is already in this sum. Do not add the 40px reserve again.
        w += actions.offsetWidth + 8;
      } else {
        w += this.columnFilterIconPx(th);
      }
      Array.from(bodyRows || []).forEach((row) => {
        const bodyCell = row.children[columnIndex];
        if (!bodyCell) return;
        const bodyCs = window.getComputedStyle(bodyCell);
        const bodyPad = this.cssPx(bodyCs.paddingLeft) + this.cssPx(bodyCs.paddingRight)
          + this.cssPx(bodyCs.borderLeftWidth) + this.cssPx(bodyCs.borderRightWidth);
        const bodyText = this.measureNowrapText(bodyCell.querySelector('span') || bodyCell);
        w = Math.max(w, bodyText + bodyPad);
      });
      return Math.ceil(w);
    },
    lockStickyCellWidth(el, px) {
      if (!el) return;
      if (this.isCheckboxColumnCell(el)) {
        const checkboxPx = `${TASKLIST_CHECKBOX_COLUMN_PX}px`;
        el.style.setProperty('width', checkboxPx, 'important');
        el.style.setProperty('min-width', checkboxPx, 'important');
        el.style.setProperty('max-width', checkboxPx, 'important');
        return;
      }
      if (!px) return;
      el.style.width = `${px}px`;
      el.style.minWidth = `${px}px`;
      el.style.setProperty('max-width', `${px}px`, 'important');
    },
    clearStickyCellWidth(el) {
      el.style.left = '';
      el.style.width = '';
      el.style.minWidth = '';
      el.style.removeProperty('max-width');
    },
    /**
     * Header layout identity. Same key while later pages append; changes on
     * role switch, cycle change, or column customize.
     * @returns {string}
     */
    columnWidthLayoutKey() {
      const headers = this.resolvedTableHeadersfilter || [];
      const identity = headers
        .map((header) => `${header?.value || ''}:${isTasklistColumnVisible(header) ? 1 : 0}`)
        .join('|');
      return `${this.projectId || ''}|${this.userType || ''}|${identity}`;
    },
    hasLockedColumnWidths() {
      return this.__lockedColumnLayoutKey === this.columnWidthLayoutKey()
        && Array.isArray(this.__lockedColumnWidths)
        && this.__lockedColumnWidths.length > 0;
    },
    resetColumnWidthLock(table) {
      this.__lockedColumnLayoutKey = '';
      this.__lockedColumnWidths = null;
      table.style.width = '';
      table.style.minWidth = '';
      table.style.tableLayout = '';
      table.querySelectorAll('th, td').forEach((el) => {
        el.classList.remove('dyn-sticky-col', 'dyn-sticky-col-edge');
        this.clearStickyCellWidth(el);
        el.removeAttribute('data-sticky-seq');
      });
    },
    /**
     * Write the locked width onto one header or body cell.
     * @param {HTMLElement} el
     * @param {{ width: number, sticky?: boolean, seq?: number, left?: number, edge?: boolean }} entry
     */
    applyLockedColumnCell(el, entry) {
      if (!el || !entry?.width) return;
      if (entry.sticky) {
        el.classList.add('dyn-sticky-col');
        el.dataset.stickySeq = String(entry.seq);
        el.style.left = `${entry.left}px`;
        el.classList.toggle('dyn-sticky-col-edge', !!entry.edge);
      }
      this.lockStickyCellWidth(el, entry.width);
    },
    applyLockedColumnWidths() {
      const locked = this.__lockedColumnWidths;
      if (!locked || !locked.length) return;
      const elements = this.getStickyTableElements();
      if (!elements) return;
      const { table, headerRow } = elements;
      const headerCells = this.headerCellsOf(headerRow);
      // A filter menu can add non-cell nodes or re-render headers. That is not
      // a new column set — never drop the lock and measure in that state.
      if (headerCells.length !== locked.length) return;
      // Checkbox stays 48px. A user drag is the saved max-width, not the
      // truncation default (280px) and not a fresh offsetWidth measure.
      locked.forEach((entry) => {
        const th = headerCells[entry.index];
        if (entry.checkbox || (th && this.isCheckboxHeader(th))) {
          entry.checkbox = true;
          entry.width = TASKLIST_CHECKBOX_COLUMN_PX;
          entry.userResized = false;
          return;
        }
        const saved = this.userResizedWidthPx(th);
        if (saved > 0) {
          entry.width = saved;
          entry.userResized = true;
        }
      });
      let left = 0;
      const stickyCount = locked.filter((entry) => entry.sticky).length;
      locked.forEach((entry) => {
        if (!entry.sticky) return;
        entry.left = left;
        entry.edge = entry.seq === stickyCount - 1;
        left += entry.width;
      });
      let tableWidth = 0;
      locked.forEach((entry) => {
        this.applyLockedColumnCell(headerCells[entry.index], entry);
        tableWidth += entry.width;
      });
      table.querySelectorAll('tbody tr').forEach((tr) => {
        locked.forEach((entry) => {
          this.applyLockedColumnCell(tr.children[entry.index], entry);
        });
      });
      table.style.tableLayout = 'fixed';
      table.style.width = `${tableWidth}px`;
      table.style.minWidth = `${tableWidth}px`;
    },
    /**
     * Lock sticky columns (checkbox 48px, Task Name 45vh, Task Type from
     * text with a 5vh floor) and freeze every other column at the width
     * already on screen. Later appends reuse this lock and do not remeasure
     * the checkbox column.
     * @returns {boolean}
     */
    measureAndLockColumnWidths() {
      if (this.__columnWidthMeasuring || this.columnMenuOpen || this.__columnResizing) return false;
      const elements = this.getStickyTableElements();
      if (!elements) return false;
      this.__columnWidthMeasuring = true;
      try {
        const { table, headerRow } = elements;
        this.resetColumnWidthLock(table);
        const targetInfo = this.getStickyHeaderTargets(headerRow);
        if (!targetInfo) {
          this.restoreStickyColumnClassesFromDataset(table);
          return false;
        }
        const headerCells = this.headerCellsOf(headerRow);
        const bodyRows = table.querySelectorAll('tbody tr:not(.data-table-action__empty-row)');
        if (!bodyRows.length) return false;

        const stickyWidthByIndex = new Map();
        targetInfo.targets.forEach((th) => {
          const idx = headerCells.indexOf(th);
          stickyWidthByIndex.set(idx, this.stickyColumnWidth(th, bodyRows, idx));
        });

        let seq = 0;
        targetInfo.targets.forEach((th) => {
          const idx = headerCells.indexOf(th);
          th.dataset.stickySeq = String(seq);
          this.lockStickyCellWidth(th, stickyWidthByIndex.get(idx));
          seq += 1;
        });

        const locked = headerCells.map((th, index) => {
          const checkbox = this.isCheckboxHeader(th);
          if (stickyWidthByIndex.has(index)) {
            return {
              index,
              checkbox,
              sticky: true,
              seq: Number(th.dataset.stickySeq),
              width: checkbox ? TASKLIST_CHECKBOX_COLUMN_PX : stickyWidthByIndex.get(index),
            };
          }
          // Width the table already assigned after sticky columns were locked.
          // Not getBoundingClientRect of a width:100% column (that stretched Task Name).
          // Never substitute the checkbox width here: filter menus also contain checkboxes.
          // Reserve the filter control for every sortable/filterable header. Do not
          // skip that add because offsetWidth already looks wider than the label.
          const measured = Math.round(th.offsetWidth);
          return {
            index,
            checkbox,
            sticky: false,
            seq: -1,
            width: checkbox
              ? TASKLIST_CHECKBOX_COLUMN_PX
              : this.nonStickyColumnWidth(th, measured),
          };
        });
        if (this.__userResizedWidthsByValue && this.__userResizedWidthsByValue.size) {
          locked.forEach((entry) => {
            if (entry.checkbox) return;
            const saved = this.userResizedWidthPx(headerCells[entry.index]);
            if (saved > 0) {
              entry.width = saved;
              entry.userResized = true;
            }
          });
        }
        if (locked.some((entry) => !entry.width)) return false;

        const stickyCount = locked.filter((entry) => entry.sticky).length;
        let left = 0;
        locked.forEach((entry) => {
          if (!entry.sticky) return;
          entry.left = left;
          entry.edge = entry.seq === stickyCount - 1;
          left += entry.width;
        });

        this.__lockedColumnWidths = locked;
        this.__lockedColumnLayoutKey = this.columnWidthLayoutKey();
        this.applyLockedColumnWidths();
        return true;
      } finally {
        this.__columnWidthMeasuring = false;
      }
    },
    applyStickyColumnClasses() {
      try {
        if (this.hasLockedColumnWidths()) {
          this.applyLockedColumnWidths();
          return;
        }
        if (this.columnMenuOpen) return;
        this.measureAndLockColumnWidths();
      } catch (_) {
        /* silent */
      }
    },
    updateStickyOffsets() {
      try {
        if (this.__headerDragging || this.__columnResizing) return;
        const elements = this.getStickyTableElements();
        if (!elements) {
          if (this.columnMenuOpen) return;
          setTimeout(() => this.updateStickyOffsets(), 100);
          return;
        }
        if (this.hasLockedColumnWidths()) {
          this.applyLockedColumnWidths();
          return;
        }
        if (this.columnMenuOpen) return;
        const locked = this.measureAndLockColumnWidths();
        if (!locked && !this.__stickyRetry) {
          this.__stickyRetry = true;
          setTimeout(() => {
            this.__stickyRetry = false;
            this.updateStickyOffsets();
          }, 150);
        }
      } catch (_) {
        /* silent */
      }
    },
    scheduleStickyUpdate() {
      if (this.__stickyScheduled || this.columnMenuOpen) return;
      this.__stickyScheduled = true;
      requestAnimationFrame(() => {
        this.__stickyScheduled = false;
        this.updateStickyOffsets();
      });
    },
    onOpenHelpdesk(task) {
      this.$refs.tasklistDrawers?.openHelpdesk(task);
    },
    onOpenLinkBot(task) {
      this.$refs.tasklistDrawers?.openLinkBot(task);
    },
    openBulkThresholdDialog() {
      this.$refs.tasklistDrawers?.openBulkThreshold();
    },
    openBulkDueDateDialog() {
      this.$refs.tasklistDrawers?.openBulkDueDate();
    },
    onBulkActionUser(type) {
      this.openBulkActionUserDialog(type);
    },
    onBulkSelectionAction(actionId) {
      if (this.bulkLocked) return;
      if (actionId === 'preparer' || actionId === 'approver') {
        this.onBulkActionUser(actionId);
        return;
      }
      if (actionId === 'bulk-update-duedate') {
        this.openBulkDueDateDialog();
        return;
      }
      if (actionId === 'bulk-edit-threshold') {
        this.openBulkThresholdDialog();
        return;
      }
      if (actionId === 'bulk-edit-tasks') {
        this.openBulkEditDialog();
        return;
      }
      if (actionId === 'approve' || actionId === 'reject') {
        this.onOpenApproveReject({
          type: actionId,
          tasks: this.selectedTasks,
        });
        return;
      }
      if (actionId === 'bulk-workpaper-upload') {
        this.openBulkWorkpaperUploadModal();
        return;
      }
      if (actionId === 'bulk-unlink-bot') {
        this.$emit('bulk-unlink-bot');
      }
    },
    openBulkActionUserDialog(type) {
      this.$refs.tasklistDrawers?.openBulkActionUser(type);
    },
    openBulkEditDialog() {
      this.$refs.tasklistDrawers?.openBulkEdit();
    },
    openBulkWorkpaperUploadDialog() {
      this.$refs.tasklistDrawers?.openBulkWorkpaperUploadModal();
    },
    openBulkWorkpaperUploadModal() {
      this.$refs.tasklistDrawers?.openBulkWorkpaperUploadModal();
    },
    onOpenUploadDrawer(task) {
      this.$refs.tasklistDrawers?.openSingleUpload(task);
    },
    onOpenBulkUpload(task) {
      this.$refs.tasklistDrawers?.openBulkUpload(task);
    },
    openRowUserAssignment(assignType, task) {
      if (!task?._id || assignType !== TASKLIST_USER_TYPES.PREPARER) return;
      this.rowUserAssignmentType = assignType;
      this.rowUserAssignmentTask = task;
      this.rowUserAssignmentMounted = true;
      this.rowUserAssignmentVisible = true;
    },
    onRowEditApprover(task) {
      this.$refs.tasklistDrawers?.openRowApproverEditor(task);
    },
    onOpenApproveReject({ type, task, tasks }) {
      const allowedTypes = [
        CHECKLIST_STATUS_ACTION.APPROVE,
        CHECKLIST_STATUS_ACTION.REJECT,
        CHECKLIST_STATUS_ACTION.RETURN_TO_PENDING,
        'approve',
        'reject',
      ];
      if (!allowedTypes.includes(type)) return;
      const resolvedTasks = Array.isArray(tasks) && tasks.length
        ? tasks
        : (task?._id ? [task] : []);
      if (!resolvedTasks.length) return;
      this.approveRejectMode = type === CHECKLIST_STATUS_ACTION.RETURN_TO_PENDING
        ? CHECKLIST_STATUS_ACTION.REJECT
        : type;
      this.approveRejectTasks = resolvedTasks;
      this.approveRejectTask = resolvedTasks.length === 1 ? resolvedTasks[0] : null;
      this.approveRejectMounted = true;
      this.approveRejectVisible = true;
    },
    onApproveRejectSaved({ bulk } = {}) {
      this.$emit('refresh');
      if (bulk) {
        this.clearSelection();
      }
    },
    openBulkChecklistUploadDialog() {
      this.$refs.tasklistDrawers?.openBulkChecklistUpload();
    },
    openBulkLinkBotDialog(taskMap) {
      this.$refs.tasklistDrawers?.openBulkLinkBot(taskMap);
    },
    closeBulkActionUserDialog() {
      this.$refs.tasklistDrawers?.closeBulkActionUser();
    },
    closeBulkEditDialog() {
      this.$refs.tasklistDrawers?.closeBulkEdit();
    },
    closeBulkWorkpaperUploadDialog() {
      this.$refs.tasklistDrawers?.closeBulkUploadModal();
      this.$refs.tasklistDrawers?.closeBulkWorkpaperUpload();
    },
    closeBulkChecklistUploadDialog() {
      this.$refs.tasklistDrawers?.closeBulkChecklistUpload();
    },
    openStatusReportDialog() {
      this.$refs.tasklistDrawers?.openStatusReport();
    },
  },
};
</script>
